import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams, generatePath } from 'react-router-dom';
import {
  formatBalance,
  ResponseError,
  responseErrorsResolver,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  changePanelState,
  clearExportBatchesState,
  setNewTransactionCount,
} from './store/slices/batches';
import TransactionList from './list';
import {
  useCreateExportBatchMutation,
  useLazyGetExportBatchesQuery,
  useLazyGetExportOutgoingListQuery,
  useLazyGetExportBatchFileQuery,
} from './store/services';
import { clearExportTransactionListState } from './store/slices/list';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const BatchCreate = (): React.JSX.Element => {
  const { bankId, accountId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [batchError, setBatchError] = useState('');
  const [createdBatchId, setCreateBatchId] = useState('');
  const [globalError, setGlobalError] = useState('');

  const {
    batches: { panelIsOpen, countNewTransactions },
    list: { selectedTransactionIds, transactions, sum, sumSelected },
    banks: { banks },
    accounts: { accounts },
  } = useAppSelector((state) => state.export);

  const [getTransactionsList, { isLoading, isFetching }] =
    useLazyGetExportOutgoingListQuery();

  const [getBatchesList] = useLazyGetExportBatchesQuery();
  const [getExportBatchFile, { isLoading: batchDownload }] =
    useLazyGetExportBatchFileQuery();

  const getBatchesListAction = useCallback(() => {
    if (bankId && accountId) {
      getBatchesList({
        pspPartnerId: bankId,
        pspAccountId: accountId,
        take: '15',
      });
    }
  }, [bankId, accountId]);

  const [create, { isLoading: createIsLoading, isSuccess }] =
    useCreateExportBatchMutation();

  const handleClose = useCallback(() => {
    dispatch(clearExportBatchesState());
    setCreateBatchId('');

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.EXPORT_BATCHES, {
          bankId: bankId as string,
          accountId: accountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  const getExportBatchFileAction = async (id: string, number?: string) => {
    const currentBankName = banks.find((item) => item.id === bankId)?.name;
    const currentAccontNumber = accounts.find(
      (item) => item.id === accountId,
    )?.name;

    const response = (await getExportBatchFile(id)) as {
      error: ResponseError;
      data: any;
    };

    if (response.data && currentBankName && currentBankName) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentBankName}, ${currentAccontNumber}, Batch ${number}.xlsx`;
      hiddenElement.click();

      handleClose();
    } else if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setBatchError,
      });
    }
  };

  const onSubmit = async () => {
    setGlobalError('');

    if (selectedTransactionIds && bankId && accountId) {
      const response = (await create({
        transactionsIds: selectedTransactionIds,
        pspPartnerId: bankId,
        pspAccountId: accountId,
      })) as {
        data: { data: { id: string; number: string } };
        error?: ResponseError;
      };

      if (response && response?.data && response?.data?.data) {
        setCreateBatchId(response?.data?.data.id);

        getExportBatchFileAction(
          response?.data?.data.id,
          response?.data?.data?.number,
        );
      } else if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError: () => {},
          setGlobalError,
        });
      }
    }
  };

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(clearExportBatchesState());
      dispatch(clearExportTransactionListState());

      getBatchesListAction();
    };
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        setNewTransactionCount(
          countNewTransactions - (selectedTransactionIds?.length as number),
        ),
      );
    }
  }, [isSuccess]);

  return (
    <SidePanel
      type={SidePanelType.Transactions}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      title="New batch"
      errorSidepanel={globalError}
      sidepanelExpandedKey="export-panel"
      footer={
        <div className={cn.exportSidepanelFooter}>
          {selectedTransactionIds?.length
            ? sumSelected && (
                <>
                  <div className={cn.exportSidepanelFooterLeft}>
                    {`${selectedTransactionIds?.length} transactions`}
                  </div>
                  <div
                    className={cn.exportSidepanelFooterRight}
                  >{`total: ${formatBalance(sumSelected)}`}</div>
                </>
              )
            : transactions?.length &&
              sum && (
                <>
                  <div className={cn.exportSidepanelFooterLeft}>
                    {`All ${transactions?.length} transactions`}
                  </div>
                  <div
                    className={cn.exportSidepanelFooterRight}
                  >{`total: ${formatBalance(sum)}`}</div>
                </>
              )}
        </div>
      }
    >
      <div className={cn.exportSidepanelWrapper}>
        <TransactionList
          isCreate
          onSubmit={onSubmit}
          getExportBatchesFile={getExportBatchFileAction}
          isLoading={isLoading || isFetching}
          requestInProgress={createIsLoading || batchDownload}
          batchError={batchError}
          createdBatchId={createdBatchId}
          getTransactionsList={getTransactionsList}
        />
      </div>
    </SidePanel>
  );
};

export default BatchCreate;
